import { Bus, MoreVertical, Radio } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { TrackedVehicle, TransportAlert } from '@/types/transport';
import { ALERT_STYLE, ROUTE_TONE } from '../constants';
import { RailCard } from './TransportSideRail';

export interface TrackingSideRailProps {
  vehicles: TrackedVehicle[];
  alerts: TransportAlert[];
  onViewAllVehicles?: () => void;
  onViewAllAlerts?: () => void;
  onVehicleMenu?: (vehicle: TrackedVehicle) => void;
}

/**
 * The Tracking tab's rail: every vehicle reporting in, and what the system is
 * warning about.
 *
 * No ring here, unlike the other tabs. A proportion answers "how is the fleet
 * doing overall", and somebody watching a live map has already decided they
 * care about individual vehicles — so the space goes to a row per bus with the
 * one number that changes minute to minute.
 */
export function TrackingSideRail({
  vehicles,
  alerts,
  onViewAllVehicles,
  onViewAllAlerts,
  onVehicleMenu,
}: TrackingSideRailProps) {
  return (
    <div className="flex flex-col gap-4">
      <RailCard
        title="Live vehicle status"
        action={onViewAllVehicles && { label: 'View all', onSelect: onViewAllVehicles }}
      >
        <ul className="flex flex-col gap-1">
          {vehicles.map((vehicle, index) => (
            <li
              key={vehicle.uuid}
              className="flex items-center gap-2.5 rounded-2xl px-2 py-2 transition-colors hover:bg-surface-2 animate-fade-up"
              style={{ animationDelay: `${Math.min(index, 6) * 50}ms` }}
            >
              <span
                className={cn(
                  'grid h-8 w-8 shrink-0 place-items-center rounded-xl',
                  ROUTE_TONE[vehicle.tone].soft,
                )}
                aria-hidden
              >
                <Bus className="h-4 w-4" />
              </span>

              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="truncate text-sm font-bold text-ink">
                    {vehicle.registrationNumber}
                  </span>
                  <span
                    className={cn(
                      'pill shrink-0 text-[11px] font-bold tracking-label',
                      ROUTE_TONE[vehicle.tone].soft,
                    )}
                  >
                    {vehicle.routeCode}
                  </span>
                </span>
                <span className="mt-0.5 flex items-center gap-1 text-xs font-semibold text-ink-3">
                  {/* The aerial says this row is live rather than last known —
                      a speed with no such mark reads as a stored figure. */}
                  <Radio className="h-3 w-3 shrink-0" aria-hidden />
                  <span className="truncate">{vehicle.routeName}</span>
                </span>
              </span>

              <span className="shrink-0 text-sm font-bold text-ink">{vehicle.speedKmph} km/h</span>

              <button
                type="button"
                onClick={() => onVehicleMenu?.(vehicle)}
                title={`More for ${vehicle.registrationNumber}`}
                aria-label={`More for ${vehicle.registrationNumber}`}
                className="grid h-8 w-8 shrink-0 place-items-center rounded-xl text-ink-3 transition-colors hover:bg-surface-3 hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      </RailCard>

      <RailCard
        title="Alerts & notifications"
        action={onViewAllAlerts && { label: 'View all', onSelect: onViewAllAlerts }}
      >
        <ul className="flex flex-col gap-1">
          {alerts.map((alert, index) => {
            const style = ALERT_STYLE[alert.kind];
            const Icon = style.icon;
            return (
              <li
                key={alert.uuid}
                className="flex items-start gap-3 rounded-2xl px-2 py-2 transition-colors hover:bg-surface-2 animate-fade-up"
                style={{ animationDelay: `${Math.min(index, 5) * 50}ms` }}
              >
                <span
                  className={cn('grid h-8 w-8 shrink-0 place-items-center rounded-xl', style.tile)}
                  aria-hidden
                >
                  <Icon className="h-4 w-4" />
                </span>

                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-sm font-bold text-ink">
                      {alert.title ?? alert.message}
                    </span>
                    <time className="shrink-0 text-xs font-bold text-ink-3">{alert.at}</time>
                  </span>
                  {alert.title && (
                    <span className="mt-0.5 block text-xs font-semibold leading-snug text-ink-2">
                      {alert.message}
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      </RailCard>
    </div>
  );
}
